import { toNumber } from "./safeData.js";

/**
 * Customer-facing price breakdown.
 *
 * The checkout manifest used to itemise `rawMarketplaceCost` ("Base Item
 * Selection Cost") and `guaranteedNetProfit` ("DHC protected net yield") — the
 * supplier cost and the margin — to the shopper, while redact_product() strips
 * exactly those fields from every catalog response.
 *
 * Renaming the rows would not have fixed it: show a total plus every component
 * except the item line and anyone can subtract their way back to the cost. So
 * the item line is *derived* from the total instead, which folds the margin into
 * it and means this function never reads the internal figures at all. The rows
 * still reconcile to the total exactly.
 */

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * @param {object} summary Global checkout summary.
 * @returns {{itemPrice: number|null, duties: number|null, shipping: number|null,
 *            total: number|null, localCurrency: string, localTotal: number|null,
 *            exchangeRate: number|null}}
 */
export function buildCustomerPriceBreakdown(summary) {
  const source = summary && typeof summary === "object" ? summary : {};

  const total = toNumber(source.customerFinalLandedPrice ?? source.priceUSD);
  const duties = toNumber(source.portFeesAndDuties, 0);

  // Freight is the difference between landed-at-port cost and the goods cost.
  const cif = toNumber(source.computedCIF);
  const goods = toNumber(source.rawMarketplaceCost);
  const shipping =
    cif !== null && goods !== null ? Math.max(0, round2(cif - goods)) : 0;

  // Item price = what remains of the total after duties and shipping. Equals
  // goods + margin, so neither is recoverable from the rows on their own.
  const itemPrice =
    total === null ? null : Math.max(0, round2(total - duties - shipping));

  const localCurrency = source.localCurrency || "USD";
  const localTotal = toNumber(source.localTotal ?? source.localPayload?.grandTotalLanded);

  return {
    itemPrice,
    duties: total === null ? null : duties,
    shipping: total === null ? null : shipping,
    total,
    localCurrency,
    localTotal: localCurrency !== "USD" ? localTotal : null,
    exchangeRate: toNumber(source.exchangeRateUsed),
  };
}

/** Keys that must never reach a customer-facing component. */
export const INTERNAL_ONLY_KEYS = Object.freeze([
  "rawMarketplaceCost",
  "guaranteedNetProfit",
  "source_price",
  "source_cost",
  "base_price",
  "profit_margin",
  "dhc_net_yield",
]);
